import { useCallback, useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

export interface UseCanvasKeyboardArgs {
  zOrder: string[];
}

export interface UseCanvasKeyboardResult {
  focusedNodeId: string | null;
  editingNodeId: string | null;
  onKeyDown: (e: ReactKeyboardEvent) => void;
}

export function useCanvasKeyboard({
  zOrder,
}: UseCanvasKeyboardArgs): UseCanvasKeyboardResult {
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(
    zOrder.length > 0 ? zOrder[0] : null,
  );
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);

  // O(1) id → position lookup. Avoids zOrder.indexOf(current) per keystroke
  // (which becomes O(n) per arrow press on a large canvas).
  const zPosMap = useMemo(
    () => new Map(zOrder.map((id, i) => [id, i])),
    [zOrder],
  );

  // Reconcile focusedNodeId with zOrder changes.
  useEffect(() => {
    if (focusedNodeId === null) {
      if (zOrder.length > 0) setFocusedNodeId(zOrder[0]);
      return;
    }
    if (!zPosMap.has(focusedNodeId)) {
      setFocusedNodeId(zOrder.length > 0 ? zOrder[0] : null);
    }
  }, [zOrder, focusedNodeId, zPosMap]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setEditingNodeId(focusedNodeId);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setEditingNodeId(null);
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      setFocusedNodeId((current) => {
        const idx = current === null ? -1 : (zPosMap.get(current) ?? -1);
        if (e.key === 'ArrowDown') {
          if (idx < 0) return zOrder[0] ?? null;
          return zOrder[Math.min(idx + 1, zOrder.length - 1)] ?? current;
        } else {
          if (idx <= 0) return zOrder[0] ?? null;
          return zOrder[idx - 1] ?? current;
        }
      });
    },
    [zOrder, zPosMap, focusedNodeId],
  );

  return { focusedNodeId, editingNodeId, onKeyDown };
}
